import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Modal, Notice } from "../../lib/ui";

/*
 * Receive goods against a purchase order – port of grn.php + grn_form.php + process_grn.php
 * (API: /api/grns/receivable, /api/grns). Stock goes up, the purchase price is updated, the PO becomes Received or
 * Partially Received, and Dr Inventory 1200 / Cr Accounts payable 2000 is posted. You can't receive more than is outstanding.
 */

export default function GrnReceive() {
  const api = useApi();
  const [pos, setPos] = useState(null);
  const [open, setOpen] = useState(null);
  const [lines, setLines] = useState([]);
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [notice, setNotice] = useState(null);
  const loadPos = () => api.get("/api/grns/receivable").then(setPos).catch((e) => setNotice({ tone: "err", text: e.message }));
  useEffect(() => { loadPos(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (po) => {
    setOpen(po); setLines([]); setQty({}); setNotes("");
    api.get(`/api/grns/receivable/${po.po_id}`).then((ls) => { setLines(ls); setQty(Object.fromEntries(ls.map((l) => [l.item_id, String(l.remaining)]))); })
      .catch((e) => setNotice({ tone: "err", text: e.message }));
  };
  const total = lines.reduce((s, l) => s + (Number(qty[l.item_id]) || 0) * Number(l.unit_price), 0);
  const submit = async () => {
    setConfirm(false);
    try {
      const r = await api.post("/api/grns", { po_id: open.po_id, items: qty, notes });
      setNotice({ tone: "ok", text: r.message }); setOpen(null); setLines([]); loadPos();
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
  };
  return (
    <main className="pg" style={{ maxWidth: "84rem" }}>
      <PageHeader slug="grn" actions={<Link to="/grn_list" className="btn"><i className="fas fa-list" /> GRN register</Link>} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <div className="grid2">
        <section className="panel"><div className="tbl-wrap"><table className="tbl">
          <thead><tr><th>PO #</th><th>Supplier</th><th>Date</th><th>Status</th><th /></tr></thead>
          <tbody>{(pos || []).map((p) => <tr key={p.po_id} style={open?.po_id === p.po_id ? { background: "var(--brand-soft)" } : undefined}>
            <td className="mono">#{p.po_id}</td><td style={{ fontWeight: 600, color: "var(--ink)" }}>{p.client_name}</td><td>{String(p.po_date).slice(0, 10)}</td>
            <td><span className={`pill-tag ${String(p.status).toLowerCase() === "approved" ? "ok" : "warn"}`}>{p.status}</span></td>
            <td><button type="button" className="btn sm primary" onClick={() => pick(p)}>Receive items</button></td></tr>)}</tbody>
        </table>{pos && pos.length === 0 && <div className="empty"><i className="fas fa-inbox" />No purchase orders waiting for goods.</div>}</div></section>
        <section className="panel card-pad">
          {!open ? <p style={{ color: "var(--muted)", margin: 0 }}>Choose a purchase order to record what arrived.</p> : (<>
            <h3>Receive goods for PO #{open.po_id} · {open.client_name}</h3>
            <table className="tbl"><thead><tr><th>Item</th><th className="num">Ordered</th><th className="num">Received</th><th className="num">Now</th><th className="num">Price</th></tr></thead>
              <tbody>{lines.map((l) => <tr key={l.item_id} style={l.remaining <= 0 ? { opacity: .55 } : undefined}><td>{l.name}</td><td className="num mono">{l.quantity}</td><td className="num mono">{l.received}</td>
                <td className="num">{l.remaining > 0 ? <input className="cell" type="number" min="0" max={l.remaining} step="1" value={qty[l.item_id] ?? ""} style={{ maxWidth: "6rem", marginLeft: "auto" }}
                  aria-label={`Quantity of ${l.name} received now`} onChange={(e) => setQty({ ...qty, [l.item_id]: e.target.value })} /> : <span className="pill-tag ok">Done</span>}</td>
                <td className="num mono">{n2(l.unit_price)}</td></tr>)}</tbody></table>
            <div className="field" style={{ marginTop: ".8rem" }}><label htmlFor="gr-n">Notes (optional)</label><input id="gr-n" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. 2 cartons damaged" /></div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1rem" }}>
              <span>Value received: <b className="mono">TZS {n2(total)}</b></span>
              <button type="button" className="btn primary" disabled={total <= 0} onClick={() => setConfirm(true)}><i className="fas fa-clipboard-check" /> Generate GRN</button></div>
          </>)}
        </section>
      </div>
      {confirm && <Modal title="Generate this GRN?" onClose={() => setConfirm(false)} actions={<><button type="button" className="btn" onClick={() => setConfirm(false)}>Cancel</button><button type="button" className="btn primary" onClick={submit}>Generate GRN</button></>}>
        Stock will go up for the items received, and TZS {n2(total)} will be recorded as owed to {open.client_name}. This can't be undone.</Modal>}
    </main>
  );
}
